// assets/js/utils/event-parts.js
/**
 * Several events on one RSVP — ceremony, dinner, brunch (task #508).
 *
 * ── The shape, and why it is this small ──────────────────────────────────
 * A part is a NAME, an optional WHEN and WHERE, and an optional LIMIT.
 *
 * It did not start that way. The original design left date, time and location
 * out on the grounds that they "already live on the wedding website's Event
 * Details page" -- which turned out to be wrong twice over: eventParts is not
 * referenced anywhere in the wedding-site code, so the two were never linked,
 * and only one of the nine hosts using parts owns a site at all. What hosts
 * actually did was type the details into the name, up against the 60-character
 * limit -- one production part name is "13:00 - 14:00 Ceremonia [Familia y
 * Amigos Cercanos]" -- and, before this feature existed, into the option
 * labels of the checkbox question they built by hand ("Ceremony only (Faith
 * Lutheran Church)", "I will be there on 8th for Reception & Sangeeth").
 * Four separate customers across eight months, all working around the same
 * absent fields.
 *
 * A guest's answer is still one NUMBER per part -- how many of their party are
 * coming -- because that is what the caps and the dashboard totals count, and
 * changing it would rewrite every stored response. The guest form no longer
 * ASKS for that number: nine hand-built versions of this question, and every
 * response ever received, modelled it as "which parts" and never as "how many
 * to each". So the form collects a tick and sends the whole party size. The
 * numeric shape is what the storage speaks; the checklist is what the guest
 * speaks, and normalizePartAttendance is the seam between them.
 *
 *   form.eventParts        [{ id, name, when, where, cap, tags, price, optIn }]  max 5
 *   contact.tags           ["family", "vip"]      who the guest is (task #847)
 *   response.partAttendance { [partId]: number }
 *   form.partAttendingCounts { [partId]: number }     denormalized, like
 *                                                     attendingGuestCount
 *
 * ── The rule that keeps the pricing honest ───────────────────────────────
 * Parts NEVER multiply anything the host has paid for. A form with five parts
 * still counts one response per guest against its allowance, and the
 * form-level capacity still binds over the top of every per-part limit. A part
 * limit can only ever make a form MORE restrictive, never less — see
 * partIsFull, which is why it takes the form cap as well.
 */

// Five is plenty for a wedding weekend and keeps this from becoming a
// general-purpose event platform bought once. See the abuse notes on #508.
const MAX_EVENT_PARTS = 5;
const MAX_PART_NAME = 60;
// When and where, for the part that is not the main event. Free text rather
// than a date picker on purpose: a host writes "Sunday" or "the morning after"
// as readily as a date, and the only consumer is a line of text under the name.
const MAX_PART_WHEN = 40;
const MAX_PART_WHERE = 80;
// ── A price per person on a part (2026-10-30) ────────────────────────────
// "The pre-wedding dinner is $70 and the cruise $120, people pay to come" -
// a free wedding with paid extras. Cents, USD (what form ticketing charges
// in), null = free. The guest still RSVPs for free: their reply is saved as
// usual and the ticked paid parts are paid for straight after, against that
// reply (ticketing.service#createPartsCheckout). A paid part starts UNTICKED,
// because a part that charges must be chosen, never defaulted into.
// The floor is Stripe's own minimum charge; the ceiling stops a typo ($12000
// for $120) from reaching a guest's card.
const MIN_PART_PRICE_CENTS = 100;
const MAX_PART_PRICE_CENTS = 1000000;
// ── Who sees a part (task #847) ──────────────────────────────────────────
// A part may carry audience TAGS ("family", "reception only"). A guest whose
// invitation carries one of them sees the part; a part with no tags is for
// everyone. Guest tags live on the campaign contact (contacts[].tags) and are
// read live through the guest's personalized link, like the household roster.
// A guest with no identity (a shared link) sees every part — the host chose a
// shared link, and hiding parts from an unknown guest would hide the event.
const MAX_PART_TAGS = 10;
const MAX_TAG_LENGTH = 30;
const MAX_CONTACT_TAGS = 20;

/** Ids are stable for the life of the part — renaming or reordering must never
 *  reassign who is coming to what. */
function newPartId() {
  return 'p' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
}

/**
 * Clean whatever the builder sent into storable parts.
 * Drops unnamed rows, trims and caps names, keeps at most MAX_EVENT_PARTS, and
 * mints an id for any row that arrived without one.
 */
function normalizeEventParts(raw) {
  if (!Array.isArray(raw)) return [];
  const seen = {};
  const out = [];
  for (const row of raw) {
    if (!row || typeof row !== 'object') continue;
    const name = String(row.name == null ? '' : row.name).trim().slice(0, MAX_PART_NAME);
    if (!name) continue;                      // an unnamed part is not a part
    let id = String(row.id || '').trim();
    if (!id || seen[id]) id = newPartId();    // never let two parts share an id
    seen[id] = true;
    const capRaw = parseInt(row.cap, 10);
    const cap = Number.isFinite(capRaw) && capRaw > 0 ? capRaw : null;
    const trim = (v, max) => String(v == null ? '' : v).trim().slice(0, max);
    // Empty strings rather than undefined on a part saved before this shipped:
    // an undefined ANYWHERE in the object rejects the whole Firestore write.
    const when = trim(row.when, MAX_PART_WHEN);
    const where = trim(row.where, MAX_PART_WHERE);
    // Always an array, never undefined (same Firestore rule as above).
    const tags = sanitizeTags(row.tags, MAX_PART_TAGS) || [];
    const price = normalizePartPrice(row.price);
    // "Guests tick it themselves": starts unticked, like a paid part. A
    // boolean, never undefined (same Firestore rule as above).
    const optIn = row.optIn === true;
    out.push({ id, name, when, where, cap, tags, price, optIn });
    if (out.length >= MAX_EVENT_PARTS) break;
  }
  return out;
}

/** A stored price in whole cents, or null for a free part. Anything below the
 *  floor is free rather than an unchargeable 30 cents; above the ceiling is
 *  capped. Null, never undefined: undefined rejects the Firestore write. */
function normalizePartPrice(raw) {
  const n = Math.round(Number(raw));
  if (!Number.isFinite(n) || n < MIN_PART_PRICE_CENTS) return null;
  return Math.min(n, MAX_PART_PRICE_CENTS);
}

/** Whole dollars as typed in the builder ("70", "70.50") to cents. */
function partPriceFromInput(text) {
  const n = parseFloat(String(text == null ? '' : text).replace(',', '.').replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) && n > 0 ? normalizePartPrice(Math.round(n * 100)) : null;
}

function isPaidPart(part) {
  return !!(part && normalizePartPrice(part.price));
}

/**
 * Does the guest form start this part UNTICKED? A paid part (it is charged
 * for) and an opt-in part ("guests tick it themselves": gregory-28x7, a
 * ladies-only kitchen tea, and parts guests left ticked without noticing -
 * 81 of 155 Yes replies had every part ticked). Either way a guest is only
 * at it by choosing it, so a reply with no answer for it counts nobody there.
 */
function partStartsUnticked(part) {
  return !!part && (part.optIn === true || isPaidPart(part));
}

function hasPaidParts(parts) {
  return normalizeEventParts(parts).some(isPaidPart);
}

/** "$70" or "$70.50": the one way a part's price is written for people. */
function formatPartPrice(cents) {
  const n = Number(cents) || 0;
  return '$' + (n % 100 === 0 ? String(n / 100) : (n / 100).toFixed(2));
}

/**
 * Seats of a part a reply has paid for: by card (paidSeats, written only by
 * the payment webhook) plus any the host marked paid by hand (markedSeats -
 * cash, a bank transfer). Kept apart so a host's mark can never erase or
 * stand in for what Stripe recorded, and a refund un-pays only card seats.
 */
function paidSeatsOf(partPayments, partId) {
  const pp = partPayments && partPayments[partId];
  if (!pp) return 0;
  const n = (v) => Math.max(0, parseInt(v, 10) || 0);
  return n(pp.paidSeats) + n(pp.markedSeats);
}

/**
 * What a reply still owes, part by part: the seats it holds on each paid part
 * minus the seats already paid for. Prices come from the FORM, never from the
 * guest, so nobody can choose what they are charged.
 *
 * @param {Array}  parts           the form's parts (normalized here)
 * @param {object} partAttendance  response.partAttendance
 * @param {object} partPayments    response.partPayments { [partId]: { paidSeats } }
 * @returns {{ lines: Array<{partId,name,price,seats}>, totalCents: number }}
 */
function partsOwed(parts, partAttendance, partPayments) {
  const lines = [];
  normalizeEventParts(parts).forEach((p) => {
    if (!isPaidPart(p)) return;
    const coming = Math.max(0, parseInt(partAttendance && partAttendance[p.id], 10) || 0);
    const seats = coming - paidSeatsOf(partPayments, p.id);
    if (seats > 0) lines.push({ partId: p.id, name: p.name, price: p.price, seats });
  });
  return { lines, totalCents: lines.reduce((n, l) => n + l.price * l.seats, 0) };
}

/** One tag, as it is compared: trimmed, single-spaced, case-folded. The host's
 *  own spelling is what gets stored and shown; this is only for matching. */
function normalizeTag(raw) {
  // Invisible direction marks and zero-width characters (typed with Hebrew
  // and Arabic tags, pasted from spreadsheets) and decomposed accents made
  // the same tag two different tags (audit 2026-10-11).
  return String(raw == null ? '' : raw).normalize('NFC')
    .replace(/[​-‏‪-‮⁦-⁩﻿]/g, '')
    .replace(/\s+/g, ' ').trim().toLowerCase();
}

/**
 * Clean a tag list from anywhere (a builder row, a CSV cell already split, a
 * campaign contact written by the client). Accepts an array or a delimited
 * string ("family, vip" / "family; vip"). Drops blanks and duplicates (by the
 * folded form), trims each to MAX_TAG_LENGTH, keeps at most `max`.
 * Returns null when nothing is left, so callers can store nothing at all.
 */
function sanitizeTags(raw, max) {
  const limit = Math.max(1, parseInt(max, 10) || MAX_CONTACT_TAGS);
  let list = raw;
  if (typeof raw === 'string') list = raw.split(/[,;،؛\n]/);
  if (!Array.isArray(list)) return null;
  const seen = {};
  const out = [];
  for (const item of list) {
    const text = String(item == null ? '' : item).replace(/\s+/g, ' ').trim().slice(0, MAX_TAG_LENGTH);
    const key = normalizeTag(text);
    if (!key || seen[key]) continue;
    seen[key] = true;
    out.push(text);
    if (out.length >= limit) break;
  }
  return out.length ? out : null;
}

/** Tags as the host types them in one box: "family, vip". */
function formatTags(tags) {
  return Array.isArray(tags) ? tags.filter(Boolean).join(', ') : '';
}

/**
 * Is an item carrying audience tags for this guest? No tags = for everyone.
 * Shared by event parts (task #847) and the wedding site's schedule blocks
 * (task #848), so both answer "who sees this" the same way.
 */
function tagsMatch(itemTags, guestTags) {
  const mine = sanitizeTags(itemTags, MAX_PART_TAGS);
  if (!mine) return true;
  const have = {};
  (sanitizeTags(guestTags, MAX_CONTACT_TAGS) || []).forEach((t) => { have[normalizeTag(t)] = true; });
  return mine.some((t) => have[normalizeTag(t)]);
}

/**
 * The parts one guest is invited to. A part with no tags is for everyone;
 * a tagged part needs one of its tags on the guest. `guestTags` null or
 * undefined means "no identity" (a shared link nobody has been recognised
 * on) and, since task #885, gets the UNTAGGED parts only - a tagged part is
 * for the guests it names, and a public link must not let anyone sign up for
 * it. This is the rule the wedding website's schedule has followed since
 * #848. Returns the full list untouched when nothing is narrowed, so
 * `restricted` tells the caller whether anything changed.
 */
function partsForGuest(parts, guestTags) {
  const all = normalizeEventParts(parts);
  const kept = all.filter((p) => tagsMatch(p.tags, guestTags || []));
  return { parts: kept, restricted: kept.length !== all.length };
}

/** Does any part carry audience tags? Only then does who-sees-what matter. */
function hasTaggedParts(parts) {
  return normalizeEventParts(parts).some((p) => Array.isArray(p.tags) && p.tags.length > 0);
}

/** Ids of the parts a guest is invited to, or null when they see everything. */
function allowedPartIds(parts, guestTags) {
  const r = partsForGuest(parts, guestTags);
  return r.restricted ? r.parts.map((p) => p.id) : null;
}

/** Does this form actually use parts? Everything downstream keys off this, so a
 *  form that has never touched the feature behaves exactly as it always did. */
function formHasParts(formData) {
  return normalizeEventParts(formData && formData.eventParts).length > 0;
}

/**
 * Clean a guest's answer against the form's parts.
 * Unknown ids are dropped (a part the host deleted), counts are clamped to the
 * party size, and anything unparseable becomes 0 rather than throwing.
 *
 * @param {object} raw      what the guest's browser sent
 * @param {Array}  parts    the form's normalized parts
 * @param {number} partySize how many people this response is for
 */
function normalizePartAttendance(raw, parts, partySize) {
  const out = {};
  const size = Math.max(0, parseInt(partySize, 10) || 0);
  (parts || []).forEach((p) => {
    const n = parseInt(raw && raw[p.id], 10);
    out[p.id] = Number.isFinite(n) && n > 0 ? Math.min(n, size) : 0;
  });
  return out;
}

/**
 * The default answer for a guest who has not touched the checklist: everyone
 * is coming to everything. This is what makes the feature easy — the common
 * case is a guest who ticks nothing extra, and a host who adds parts should
 * not silently lose the headcount of every guest who ignored them.
 */
function defaultPartAttendance(parts, partySize) {
  const out = {};
  const size = Math.max(0, parseInt(partySize, 10) || 0);
  // Except a part guests choose themselves (paid, or opt-in): nobody is
  // signed up for it by not answering.
  (parts || []).forEach((p) => { out[p.id] = partStartsUnticked(p) ? 0 : size; });
  return out;
}

/** How many people this response brings to one part. */
function countForPart(response, partOrId, fallbackPartySize) {
  const part = partOrId && typeof partOrId === 'object' ? partOrId : null;
  const partId = part ? part.id : partOrId;
  const map = response && response.partAttendance;
  if (!map || typeof map !== 'object' || !(partId in map)) {
    // A part only some guests are invited to (tags) is opt-in: a reply with
    // no answer for it - an imported row, a walk-in, a reply from before the
    // part existed - counts there only if its own imported groups invite it.
    // It counted in full, so "family only, limit 30" read 180/30 and showed
    // as Full to the family it was for (audit 2026-10-11). Callers pass the
    // part object for this; an id alone keeps the old rule.
    if (part && Array.isArray(part.tags) && part.tags.length) {
      const own = Array.isArray(response && response.inviteTags) ? response.inviteTags : [];
      if (!tagsMatch(part.tags, own)) return 0;
    }
    // A paid or opt-in part too: a reply that never chose it is not there.
    if (part && partStartsUnticked(part)) return 0;
    // A response saved before the host added parts counts in full — it was
    // given for the whole event, which is what the host was asking then.
    return Math.max(0, parseInt(fallbackPartySize, 10) || 0);
  }
  const n = parseInt(map[partId], 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * Is this part full for a party of `incoming` more people?
 *
 * Takes the FORM's remaining capacity too, because a part limit may only ever
 * tighten the form's own limit — never raise it. Passing null for either means
 * "no limit from that side".
 */
function partIsFull(part, alreadyAttending, incoming, formRemaining) {
  const add = Math.max(0, parseInt(incoming, 10) || 0);
  if (formRemaining !== null && formRemaining !== undefined && add > formRemaining) return true;
  if (!part || !part.cap) return false;
  const used = Math.max(0, parseInt(alreadyAttending, 10) || 0);
  return used + add > part.cap;
}

/** Seats left on a part, or null when it has no limit. */
function partRemaining(part, alreadyAttending) {
  if (!part || !part.cap) return null;
  return Math.max(0, part.cap - Math.max(0, parseInt(alreadyAttending, 10) || 0));
}

/**
 * The full parts a guest asked to wait for, as { partId: headcount }.
 *
 * A waiting list is a LIST for the host, never a seat: nothing here is counted
 * by a limit, a total or the capacity gate, and nobody is moved in or emailed
 * automatically. The host moves a guest in by ticking the part on their row.
 * Only parts with a limit, only parts the guest is not already coming to, only
 * parts they may see (`allowedIds`, null = all), counts clamped to the party.
 * Asked for by a host whose cruise filled up (2026-10-12): a full part was a
 * dead end, and nobody knew who still wanted in when seats came free.
 */
function normalizePartWaitlist(raw, parts, partySize, attendance, allowedIds) {
  const out = {};
  if (!raw || typeof raw !== 'object') return out;
  const size = Math.max(0, parseInt(partySize, 10) || 0);
  if (!size) return out;
  const allowed = Array.isArray(allowedIds) ? new Set(allowedIds) : null;
  (parts || []).forEach((p) => {
    if (!p || !p.cap) return;
    if (allowed && !allowed.has(p.id)) return;
    if (attendance && (parseInt(attendance[p.id], 10) || 0) > 0) return;
    const n = parseInt(raw[p.id], 10);
    if (Number.isFinite(n) && n > 0) out[p.id] = Math.min(n, size);
  });
  return out;
}

/** How many of this reply's party wait for the part (0 when none). */
function waitingForPart(response, partId) {
  const map = response && response.partWaitlist;
  if (!map || typeof map !== 'object') return 0;
  const n = parseInt(map[partId], 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * Who of this reply's party goes to the part, by name (task #879 r3), or
 * null when the reply cannot say. The whole-party view saves each person's
 * ticked parts on their name entry (individualNames[].parts); without that
 * only a headcount per part is stored, which names people only when it is
 * the whole party. The receipt email and the thank-you screen both list it.
 */
function peopleForPart(response, partId, count) {
  const clean = (v) => String(v == null ? '' : v).replace(/\s+/g, ' ').trim();
  const entries = (Array.isArray(response && response.individualNames) ? response.individualNames : [])
    .filter((e) => e && clean(e.name));
  if (entries.some((e) => Array.isArray(e.parts))) {
    const names = entries.filter((e) => Array.isArray(e.parts) && e.parts.includes(partId)).map((e) => clean(e.name));
    return names.length ? names : null;
  }
  const seen = new Set();
  const everyone = [response && response.guestName].concat(entries.map((e) => e.name)).map(clean)
    .filter((n) => n && !seen.has(n.toLowerCase()) && seen.add(n.toLowerCase()));
  return everyone.length > 1 && everyone.length === count ? everyone : null;
}

const EventParts = {
  MAX_EVENT_PARTS,
  hasTaggedParts,
  MAX_PART_NAME,
  MAX_PART_WHEN,
  MAX_PART_WHERE,
  MAX_PART_TAGS,
  MAX_TAG_LENGTH,
  MAX_CONTACT_TAGS,
  MIN_PART_PRICE_CENTS,
  MAX_PART_PRICE_CENTS,
  normalizePartPrice,
  partPriceFromInput,
  isPaidPart,
  partStartsUnticked,
  hasPaidParts,
  formatPartPrice,
  paidSeatsOf,
  partsOwed,
  newPartId,
  normalizeTag,
  sanitizeTags,
  formatTags,
  tagsMatch,
  partsForGuest,
  allowedPartIds,
  normalizeEventParts,
  formHasParts,
  normalizePartAttendance,
  defaultPartAttendance,
  countForPart,
  partIsFull,
  partRemaining,
  normalizePartWaitlist,
  waitingForPart,
  peopleForPart
};

// Dual-environment, matching assets/js/utils/guest-counts.js: the browser gets
// the globals, the server requires this same file. One implementation — a
// second copy is how the surfaces drifted apart in the first place.
if (typeof window !== 'undefined') {
  window.EventParts = EventParts;
}
if (typeof module !== 'undefined' && module.exports) module.exports = EventParts;
